// @flow

import React, { PureComponent } from 'react';
import * as Yup from 'yup';
import { createFragmentContainer, graphql, QueryRenderer } from 'react-relay';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import UpdateIntegrationMutation from 'main-app/mutations/UpdateIntegration';
import RevokeOauthAccessMutation from 'main-app/mutations/RevokeOauthAccess';
import * as Actions from 'main-app/store/Actions';
import {
  Formik,
  Form,
  FieldGroup,
  FormAutoSaver,
} from 'shared/components/form';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Button from 'shared/components/common/Button';
import Panel from 'shared/components/common/Panel';
import Loader from 'shared/components/common/Loader';
import type { IntegrationForm_integration as IntegrationFragment } from './__generated__/IntegrationForm_integration';

/*
{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "type": "object",
  "integrationType": "oauth2",
  "properties": {
    "orgId": {
      "type": "string",
      "title": "Org ID",
      "description": "Your ZoHo Inventory Organization ID"
    },
    "authToken": {
      "type": "string",
      "title": "Auth Token",
      "description": "Your ZoHo Inventory API Auth Token"
    }
  },
  "required": ["orgId", "authToken"]
}
*/

type Props = {
  integration: IntegrationFragment,
};

type State = {
  isOauthAuthorized: boolean,
};

const FormWrapper = styled.div`
  @media (min-width: 1000px) {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-start;
  }
`;

const FormPanelWrapper = styled.div`
  padding-bottom: 24px;
  flex: 1;

  @media (min-width: 1000px) {
    padding: ${p => (p.left ? '0 16px 0 0' : '0 0 0 16px')};
  }
`;

class IntegrationForm extends PureComponent<Props, State> {
  validationSchema: Object;
  initialValues: Object;

  constructor(props: Props) {
    super(props);

    const {
      validationSchema,
      initialValues,
    } = this.buildMetadataFormProperties();

    this.validationSchema = Yup.object().shape({
      ...validationSchema,
      isEnabled: Yup.boolean().nullable(),
    });
    this.initialValues = {
      ...initialValues,
      isEnabled: props.integration.isEnabled,
    };

    this.state = {
      isOauthAuthorized: !!props.integration.metadata.isAuthorized,
    };
  }

  handleConnectOauth = uri => {
    window.open(uri, '_blank');
  };

  handleRevokeOauthAccess = async integration => {
    const revokeResponse = await RevokeOauthAccessMutation.commit({
      variables: {
        input: {
          integrationId: integration.id,
        },
      },
    });

    if (revokeResponse.revokeOauthAccess.success) {
      Actions.alertNotification(
        i18n.t('Your Integration Has Been Revoked!'),
        'success',
      );

      this.setState({
        isOauthAuthorized: false,
      });
    } else {
      Actions.alertNotification(
        i18n.t('Failed to Revoke Your Integration!'),
        'error',
      );
    }
  };

  buildMetadataFormProperties() {
    const { integration } = this.props;
    const { metadataSchema } = integration.source;
    const validationSchema: Object = {};
    const initialValues = {};

    if (!metadataSchema || !metadataSchema.properties) {
      return {
        validationSchema: {},
        initialValues: {},
      };
    }

    // For oauth2 integrations (like Xero), property fields such as
    // paymentAccountCode are stored in rawMetadata to avoid overwriting
    // encrypted OAuth tokens. For non-oauth2 integrations (like Squarespace)
    // they are stored in metadata as before.
    const isOauth2 = metadataSchema.integrationType === 'oauth2';
    const sourceData = isOauth2
      ? integration.rawMetadata || {}
      : integration.metadata || {};

    for (const key in metadataSchema.properties) {
      const prop = metadataSchema.properties[key];
      if (prop.type === 'string') {
        validationSchema[key] = Yup.string();
        initialValues[key] = sourceData[key] || prop.default || '';
        // TODO: support different field types
      } else {
        validationSchema[key] = Yup.string();
      }

      if (metadataSchema.required && metadataSchema.required.includes(key)) {
        validationSchema[key] = validationSchema[key].required('Required');
      }
    }

    return {
      validationSchema: validationSchema,
      initialValues,
    };
  }

  renderMetadataField = (key, metadataProperty, error) => {
    return (
      <FieldGroup
        key={key}
        name={key}
        label={metadataProperty.title || key}
        description={metadataProperty.description}
        error={error}
        // Masks secrets (e.g. QC's API key) so they aren't shown in plaintext
        // once loaded back from the (decrypted) stored metadata.
        type={metadataProperty.format === 'password' ? 'password' : undefined}
      />
    );
  };

  renderOauth2Fields = integration => {
    const { isOauthAuthorized } = this.state;

    if (isOauthAuthorized) {
      // This customer already authorized third party service access
      return (
        <div>
          <div>
            {i18n.t(`You are connected to ${integration.source.name}.`)}
          </div>
          <Button
            onClick={this.handleRevokeOauthAccess.bind(this, integration)} // eslint-disable-line
            width="200"
          >
            {i18n.t(`Revoke Access`)}
          </Button>
        </div>
      );
    }

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        variables={{
          integrationId: integration.id,
        }}
        query={graphql`
          query IntegrationFormQuery($integrationId: ID!) {
            oauthRedirectUri(integrationId: $integrationId) {
              uri
            }
          }
        `}
        render={({ props }: *) => {
          if (!props) {
            return <Loader />;
          }

          return (
            <Button
              onClick={this.handleConnectOauth.bind(  // eslint-disable-line
                this,
                props.oauthRedirectUri.uri,
              )}
              width="200"
            >
              {i18n.t(`Connect to ${integration.source.name}`)}
            </Button>
          );
        }}
      />
    );
  };

  render() {
    const { integration } = this.props;
    const { metadataSchema } = integration.source;

    return (
      <Formik
        validationSchema={this.validationSchema}
        initialValues={this.initialValues}
        onSubmit={() => {}}
        render={({ errors, isValid }) => (
          <>
            <FormAutoSaver
              onSave={async values => {
                const { isEnabled, ...fieldValues } = values;
                const { metadataSchema: schema } = integration.source;
                const isOauth2 = schema && schema.integrationType === 'oauth2';

                // For oauth2 integrations, property fields (e.g. paymentAccountCode)
                // go into rawMetadata so OAuth tokens are never overwritten.
                // For non-oauth2 integrations, fields go into metadata as before.
                let metadataInput = {};
                let rawMetadataInput = {};
                if (Object.keys(fieldValues).length > 0) {
                  if (isOauth2) {
                    rawMetadataInput = fieldValues;
                  } else {
                    metadataInput = fieldValues;
                  }
                }

                const input = {
                  id: integration.id,
                  isEnabled,
                  ...(Object.keys(metadataInput).length > 0 ? { metadata: metadataInput } : {}),
                  ...(Object.keys(rawMetadataInput).length > 0 ? { rawMetadata: rawMetadataInput } : {}),
                };

                try {
                  await UpdateIntegrationMutation.commit({
                    variables: { input },
                  });

                  Actions.alertNotification(
                    i18n.t('Your Integration Has Been Updated!'),
                    'success',
                  );
                } catch (e) {
                  Actions.alertNotification(e.message, 'error');
                }
              }}
            />
            <Form>
              <FormWrapper>
                <FormPanelWrapper left>
                  <Panel title={i18n.t('Integration Settings')}>
                    {metadataSchema && metadataSchema.properties
                      ? Object.keys(metadataSchema.properties).map(key =>
                          this.renderMetadataField(
                            key,
                            metadataSchema.properties[key],
                            errors[key],
                          ),
                        )
                      : null}
                    {metadataSchema &&
                    metadataSchema.integrationType === 'oauth2'
                      ? this.renderOauth2Fields(integration)
                      : null}
                  </Panel>
                </FormPanelWrapper>
                <FormPanelWrapper>
                  <Panel title={i18n.t('Status')}>
                    <FieldGroup
                      type="toggle"
                      name="isEnabled"
                      label={i18n.t('Integration Enabled')}
                      error={errors.isEnabled}
                    />
                  </Panel>
                </FormPanelWrapper>
              </FormWrapper>
            </Form>
          </>
        )}
      />
    );
  }
}

export default createFragmentContainer(IntegrationForm, {
  integration: graphql`
    fragment IntegrationForm_integration on Integration {
      id
      metadata
      rawMetadata
      isEnabled
      source {
        id
        name
        metadataSchema
      }
    }
  `,
});
